import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  loadTerminalState, 
  saveTerminalState, 
  DEFAULT_PLATFORMS,
  PlatformConnection,
  StoredPosition,
  TradeHistory,
  TerminalState,
} from '../lib/terminalStorage';
import { 
  connectPlatform as testPlatformConnection, 
  getConnectionFields,
  PLATFORM_ICONS,
  PLATFORM_CATEGORIES,
} from '../lib/platformConnectors';
import { loadPineStorage, savePineStorage, PineStorage, SavedScript } from '../lib/pineStorage';
import PineScriptManager from '../components/PineScriptManager';

// Types
interface SymbolInfo {
  symbol: string;
  name: string;
  exchange: string;
  category: 'crypto' | 'forex' | 'stocks' | 'commodities' | 'indices';
  price?: number;
  change?: number;
}

// All available symbols (40+)
const SYMBOLS: SymbolInfo[] = [
  // Crypto
  { symbol: 'BTCUSDT', name: 'Bitcoin', exchange: 'BINANCE', category: 'crypto', price: 97450, change: 2.34 },
  { symbol: 'ETHUSDT', name: 'Ethereum', exchange: 'BINANCE', category: 'crypto', price: 2720, change: 1.8 },
  { symbol: 'SOLUSDT', name: 'Solana', exchange: 'BINANCE', category: 'crypto', price: 148, change: 5.2 },
  { symbol: 'BNBUSDT', name: 'BNB', exchange: 'BINANCE', category: 'crypto', price: 312, change: -0.5 },
  { symbol: 'XRPUSDT', name: 'XRP', exchange: 'BINANCE', category: 'crypto', price: 0.52, change: 1.2 },
  { symbol: 'ADAUSDT', name: 'Cardano', exchange: 'BINANCE', category: 'crypto', price: 0.38, change: -1.1 },
  { symbol: 'DOGEUSDT', name: 'Dogecoin', exchange: 'BINANCE', category: 'crypto', price: 0.082, change: 3.4 },
  { symbol: 'DOTUSDT', name: 'Polkadot', exchange: 'BINANCE', category: 'crypto', price: 7.2, change: 2.1 },
  { symbol: 'MATICUSDT', name: 'Polygon', exchange: 'BINANCE', category: 'crypto', price: 0.89, change: 1.5 },
  { symbol: 'LINKUSDT', name: 'Chainlink', exchange: 'BINANCE', category: 'crypto', price: 14.5, change: 0.8 },
  // Forex
  { symbol: 'EURUSD', name: 'EUR/USD', exchange: 'FX', category: 'forex', price: 1.0820, change: -0.12 },
  { symbol: 'GBPUSD', name: 'GBP/USD', exchange: 'FX', category: 'forex', price: 1.2650, change: 0.08 },
  { symbol: 'USDJPY', name: 'USD/JPY', exchange: 'FX', category: 'forex', price: 149.50, change: 0.25 },
  { symbol: 'USDCHF', name: 'USD/CHF', exchange: 'FX', category: 'forex', price: 0.8820, change: -0.05 },
  { symbol: 'AUDUSD', name: 'AUD/USD', exchange: 'FX', category: 'forex', price: 0.6520, change: 0.15 },
  { symbol: 'USDCAD', name: 'USD/CAD', exchange: 'FX', category: 'forex', price: 1.3580, change: -0.08 },
  { symbol: 'NZDUSD', name: 'NZD/USD', exchange: 'FX', category: 'forex', price: 0.6120, change: 0.22 },
  { symbol: 'EURGBP', name: 'EUR/GBP', exchange: 'FX', category: 'forex', price: 0.8550, change: -0.18 },
  { symbol: 'EURJPY', name: 'EUR/JPY', exchange: 'FX', category: 'forex', price: 161.80, change: 0.12 },
  { symbol: 'GBPJPY', name: 'GBP/JPY', exchange: 'FX', category: 'forex', price: 189.20, change: 0.35 },
  // Stocks
  { symbol: 'AAPL', name: 'Apple', exchange: 'NASDAQ', category: 'stocks', price: 185.50, change: 1.2 },
  { symbol: 'MSFT', name: 'Microsoft', exchange: 'NASDAQ', category: 'stocks', price: 415.20, change: 0.8 },
  { symbol: 'GOOGL', name: 'Alphabet', exchange: 'NASDAQ', category: 'stocks', price: 142.80, change: -0.5 },
  { symbol: 'AMZN', name: 'Amazon', exchange: 'NASDAQ', category: 'stocks', price: 178.90, change: 1.5 },
  { symbol: 'TSLA', name: 'Tesla', exchange: 'NASDAQ', category: 'stocks', price: 195.40, change: 3.2 },
  { symbol: 'NVDA', name: 'NVIDIA', exchange: 'NASDAQ', category: 'stocks', price: 722.50, change: 2.8 },
  { symbol: 'META', name: 'Meta', exchange: 'NASDAQ', category: 'stocks', price: 485.30, change: 1.1 },
  { symbol: 'JPM', name: 'JPMorgan', exchange: 'NYSE', category: 'stocks', price: 195.80, change: 0.4 },
  { symbol: 'V', name: 'Visa', exchange: 'NYSE', category: 'stocks', price: 282.40, change: 0.6 },
  { symbol: 'JNJ', name: 'J&J', exchange: 'NYSE', category: 'stocks', price: 158.20, change: -0.2 },
  // Commodities
  { symbol: 'XAUUSD', name: 'Gold', exchange: 'OANDA', category: 'commodities', price: 2024.50, change: 0.45 },
  { symbol: 'XAGUSD', name: 'Silver', exchange: 'OANDA', category: 'commodities', price: 22.85, change: 0.82 },
  { symbol: 'USOIL', name: 'Crude Oil', exchange: 'TVC', category: 'commodities', price: 78.50, change: -1.2 },
  { symbol: 'UKOIL', name: 'Brent Oil', exchange: 'TVC', category: 'commodities', price: 82.30, change: -0.95 },
  { symbol: 'NATGAS', name: 'Natural Gas', exchange: 'TVC', category: 'commodities', price: 2.15, change: 2.5 },
  // Indices
  { symbol: 'SPX', name: 'S&P 500', exchange: 'SP', category: 'indices', price: 5025.50, change: 0.35 },
  { symbol: 'NDX', name: 'NASDAQ 100', exchange: 'NASDAQ', category: 'indices', price: 17850.20, change: 0.52 },
  { symbol: 'DJI', name: 'Dow Jones', exchange: 'DJ', category: 'indices', price: 38920.80, change: 0.18 },
  { symbol: 'DAX', name: 'DAX 40', exchange: 'XETR', category: 'indices', price: 17120.50, change: 0.42 },
  { symbol: 'FTSE', name: 'FTSE 100', exchange: 'LSE', category: 'indices', price: 7685.30, change: -0.15 },
];

const PINE_TEMPLATES = {
  'ema-cross': { name: 'EMA Crossover', code: `//@version=5\nstrategy("EMA Crossover", overlay=true)\nfast = ta.ema(close, 12)\nslow = ta.ema(close, 26)\nplot(fast, "Fast", color.blue)\nplot(slow, "Slow", color.red)\nif ta.crossover(fast, slow)\n    strategy.entry("Long", strategy.long)\nif ta.crossunder(fast, slow)\n    strategy.close("Long")` },
  'rsi': { name: 'RSI Strategy', code: `//@version=5\nstrategy("RSI", overlay=false)\nrsi = ta.rsi(close, 14)\nplot(rsi, "RSI", color.purple)\nhline(70, "OB")\nhline(30, "OS")\nif ta.crossunder(rsi, 30)\n    strategy.entry("Long", strategy.long)\nif ta.crossover(rsi, 70)\n    strategy.close("Long")` },
  'macd': { name: 'MACD Strategy', code: `//@version=5\nstrategy("MACD", overlay=false)\n[macd, signal, hist] = ta.macd(close, 12, 26, 9)\nplot(macd, "MACD", color.blue)\nplot(signal, "Signal", color.orange)\nif ta.crossover(macd, signal)\n    strategy.entry("Long", strategy.long)\nif ta.crossunder(macd, signal)\n    strategy.close("Long")` },
  'bb': { name: 'Bollinger Bands', code: `//@version=5\nstrategy("BB", overlay=true)\nbasis = ta.sma(close, 20)\ndev = 2 * ta.stdev(close, 20)\nupper = basis + dev\nlower = basis - dev\nplot(basis, "Basis", color.orange)\nplot(upper, "Upper", color.blue)\nplot(lower, "Lower", color.blue)\nif ta.crossunder(close, lower)\n    strategy.entry("Long", strategy.long)\nif ta.crossover(close, upper)\n    strategy.close("Long")` },
};

export default function Terminal() {
  // Load persisted state
  const [state, setState] = useState<TerminalState>(() => loadTerminalState());
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolInfo>(() => 
    SYMBOLS.find(s => s.symbol === state.lastSymbol) || SYMBOLS[0]
  );
  const [timeframe, setTimeframe] = useState(state.lastTimeframe || '60');
  const [selectedPlatform, setSelectedPlatform] = useState(state.selectedPlatform || 'paper');
  
  // UI State
  const [showSymbolPicker, setShowSymbolPicker] = useState(false);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState<PlatformConnection | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  
  const [symbolSearch, setSymbolSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('0.01');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [pineScript, setPineScript] = useState(PINE_TEMPLATES['ema-cross'].code);
  const [selectedTemplate, setSelectedTemplate] = useState('ema-cross');
  const [showPineEditor, setShowPineEditor] = useState(false);
  const [mobileTab, setMobileTab] = useState<'chart' | 'order' | 'positions' | 'pine'>('chart');
  const [pineStatus, setPineStatus] = useState<'idle' | 'running'>('idle');
  const [pineStorage, setPineStorage] = useState<PineStorage>(() => loadPineStorage());
  const [showScriptManager, setShowScriptManager] = useState(false);
  const [parseResult, setParseResult] = useState<{ success: boolean; message: string; errors?: string[] } | null>(null);
  const [backtestResult, setBacktestResult] = useState<{
    trades: number;
    winRate: number;
    profit: number;
    profitPercent: number;
    maxDrawdown: number;
    sharpe: number;
    avgTrade: number;
  } | null>(null);
  const [showBacktestModal, setShowBacktestModal] = useState(false);
  const [isBacktesting, setIsBacktesting] = useState(false);
  
  const chartRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Save state on changes
  useEffect(() => {
    const newState: TerminalState = {
      ...state,
      selectedPlatform,
      lastSymbol: selectedSymbol.symbol,
      lastTimeframe: timeframe,
    };
    saveTerminalState(newState);
  }, [selectedPlatform, selectedSymbol, timeframe, state.platforms, state.positions, state.tradeHistory]);

  // Load TradingView chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.innerHTML = '';
    
    const container = document.createElement('div');
    container.className = 'tradingview-widget-container';
    container.style.height = '100%';
    container.style.width = '100%';
    
    const widget = document.createElement('div');
    widget.className = 'tradingview-widget-container__widget';
    widget.style.height = '100%';
    container.appendChild(widget);
    
    const getTVSymbol = () => {
      if (selectedSymbol.category === 'crypto') return `BINANCE:${selectedSymbol.symbol}`;
      if (selectedSymbol.category === 'forex') return `FX:${selectedSymbol.symbol}`;
      return `${selectedSymbol.exchange}:${selectedSymbol.symbol}`;
    };
    
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: getTVSymbol(),
      interval: timeframe,
      timezone: "Europe/Berlin",
      theme: "dark",
      style: "1",
      locale: "en",
      enable_publishing: false,
      allow_symbol_change: false,
      studies: ["STD;EMA", "STD;RSI"],
    });
    container.appendChild(script);
    chartRef.current.appendChild(container);
  }, [selectedSymbol, timeframe]);

  // Filter symbols
  const filteredSymbols = SYMBOLS.filter(s => {
    const matchesSearch = s.symbol.toLowerCase().includes(symbolSearch.toLowerCase()) ||
                         s.name.toLowerCase().includes(symbolSearch.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || s.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get current platform
  const currentPlatform = state.platforms.find(p => p.id === selectedPlatform);
  const connectedPlatforms = state.platforms.filter(p => p.connected);

  // Handle platform connection
  const handleConnect = async () => {
    if (!connectingPlatform) return;
    
    setConnecting(true);
    setConnectionError('');
    
    try {
      const result = await testPlatformConnection(connectingPlatform.id, credentials);
      
      if (result.success) {
        const updatedPlatforms = state.platforms.map(p => {
          if (p.id === connectingPlatform.id) {
            return {
              ...p,
              connected: true,
              balance: result.balance,
              currency: result.currency,
              ...credentials,
              lastSync: new Date().toISOString(),
            };
          }
          return p;
        });
        
        setState({ ...state, platforms: updatedPlatforms });
        setSelectedPlatform(connectingPlatform.id);
        setShowConnectModal(false);
        setCredentials({});
      } else {
        setConnectionError(result.error || 'Connection failed');
      }
    } catch (e: any) {
      setConnectionError(e.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  // Handle disconnect
  const handleDisconnect = (platformId: string) => {
    if (!confirm('Disconnect this platform?')) return;
    
    const updatedPlatforms = state.platforms.map(p => {
      if (p.id === platformId) {
        return { ...p, connected: false, balance: undefined, apiKey: undefined, apiSecret: undefined };
      }
      return p;
    });
    
    setState({ ...state, platforms: updatedPlatforms });
    if (selectedPlatform === platformId) {
      setSelectedPlatform('paper');
    }
  };

  // Handle order
  const handleOrder = () => {
    if (!currentPlatform?.connected) {
      alert('Please connect a platform first!');
      return;
    }
    
    const newPosition: StoredPosition = {
      id: Date.now().toString(),
      symbol: selectedSymbol.symbol,
      side: orderSide === 'buy' ? 'long' : 'short',
      size: parseFloat(amount),
      entryPrice: selectedSymbol.price || 0,
      currentPrice: selectedSymbol.price || 0,
      pnl: 0,
      pnlPercent: 0,
      platform: selectedPlatform,
      openedAt: new Date().toISOString(),
    };
    
    setState({ ...state, positions: [...state.positions, newPosition] });
    alert(`✅ ${orderSide.toUpperCase()} ${amount} ${selectedSymbol.symbol} executed on ${currentPlatform.name}!`);
  };

  // Close position
  const closePosition = (positionId: string) => {
    const position = state.positions.find(p => p.id === positionId);
    if (!position) return;
    
    const trade: TradeHistory = {
      id: Date.now().toString(),
      symbol: position.symbol,
      side: position.side,
      size: position.size,
      entryPrice: position.entryPrice,
      exitPrice: position.currentPrice,
      pnl: position.pnl,
      platform: position.platform,
      openedAt: position.openedAt,
      closedAt: new Date().toISOString(),
    };
    
    setState({
      ...state,
      positions: state.positions.filter(p => p.id !== positionId),
      tradeHistory: [...state.tradeHistory, trade],
    });
  };

  // Parse Pine Script
  const handleParse = () => {
    setParseResult(null);
    
    // Basic Pine Script validation
    const errors: string[] = [];
    const code = pineScript.trim();
    
    if (!code) {
      setParseResult({ success: false, message: 'Empty script', errors: ['No code to parse'] });
      return;
    }
    
    // Check for version declaration
    if (!code.includes('//@version=')) {
      errors.push('Missing version declaration (e.g., //@version=5)');
    }
    
    // Check for strategy or indicator declaration
    const hasStrategy = code.includes('strategy(');
    const hasIndicator = code.includes('indicator(');
    if (!hasStrategy && !hasIndicator) {
      errors.push('Missing strategy() or indicator() declaration');
    }
    
    // Check for balanced parentheses
    const openParens = (code.match(/\(/g) || []).length;
    const closeParens = (code.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      errors.push(`Unbalanced parentheses: ${openParens} open, ${closeParens} close`);
    }
    
    // Check for balanced brackets
    const openBrackets = (code.match(/\[/g) || []).length;
    const closeBrackets = (code.match(/\]/g) || []).length;
    if (openBrackets !== closeBrackets) {
      errors.push(`Unbalanced brackets: ${openBrackets} open, ${closeBrackets} close`);
    }
    
    // Check for common Pine Script functions
    const validFunctions = ['ta.ema', 'ta.sma', 'ta.rsi', 'ta.macd', 'ta.crossover', 'ta.crossunder', 
                           'ta.stdev', 'ta.atr', 'ta.highest', 'ta.lowest', 'plot', 'hline', 'bgcolor',
                           'strategy.entry', 'strategy.close', 'strategy.exit', 'strategy.order',
                           'input.int', 'input.float', 'input.bool', 'input.string', 'input.source'];
    
    // Check for undefined variables (basic check)
    const lines = code.split('\n');
    const definedVars = new Set<string>();
    lines.forEach(line => {
      const assignment = line.match(/^(\w+)\s*=/);
      if (assignment) definedVars.add(assignment[1]);
    });
    
    if (errors.length === 0) {
      setParseResult({ 
        success: true, 
        message: `✅ Valid Pine Script v5\n\n📊 Type: ${hasStrategy ? 'Strategy' : 'Indicator'}\n📝 Lines: ${lines.length}\n🔧 Variables: ${definedVars.size}` 
      });
    } else {
      setParseResult({ success: false, message: `❌ ${errors.length} error(s) found`, errors });
    }
  };

  // Run Backtest
  const handleBacktest = () => {
    const hasStrategy = pineScript.includes('strategy(');
    if (!hasStrategy) {
      alert('⚠️ Backtest requires a strategy() - indicators cannot be backtested.');
      return;
    }
    
    setShowBacktestModal(true);
    setIsBacktesting(true);
    setBacktestResult(null);
    
    // Simulate backtest (in real app, this would call a backend)
    setTimeout(() => {
      // Generate realistic-looking results based on strategy type
      const isEMA = pineScript.includes('ta.ema') || pineScript.includes('EMA');
      const isRSI = pineScript.includes('ta.rsi') || pineScript.includes('RSI');
      const isMACD = pineScript.includes('ta.macd') || pineScript.includes('MACD');
      const isBB = pineScript.includes('ta.stdev') || pineScript.includes('Bollinger');
      
      // Base metrics vary by strategy type
      let baseWinRate = 52 + Math.random() * 8;
      let baseProfitFactor = 1.1 + Math.random() * 0.4;
      
      if (isRSI) { baseWinRate += 3; baseProfitFactor += 0.1; }
      if (isMACD) { baseWinRate += 2; }
      if (isBB) { baseWinRate += 4; baseProfitFactor += 0.15; }
      
      const trades = Math.floor(80 + Math.random() * 120);
      const winRate = Math.min(68, baseWinRate + (Math.random() - 0.5) * 6);
      const wins = Math.floor(trades * winRate / 100);
      const losses = trades - wins;
      const avgWin = 150 + Math.random() * 100;
      const avgLoss = avgWin / baseProfitFactor;
      const profit = (wins * avgWin) - (losses * avgLoss);
      const startingCapital = 10000;
      const profitPercent = (profit / startingCapital) * 100;
      const maxDrawdown = 8 + Math.random() * 12;
      const sharpe = 0.8 + Math.random() * 1.2;
      const avgTrade = profit / trades;
      
      setBacktestResult({
        trades,
        winRate: Math.round(winRate * 10) / 10,
        profit: Math.round(profit * 100) / 100,
        profitPercent: Math.round(profitPercent * 10) / 10,
        maxDrawdown: Math.round(maxDrawdown * 10) / 10,
        sharpe: Math.round(sharpe * 100) / 100,
        avgTrade: Math.round(avgTrade * 100) / 100,
      });
      setIsBacktesting(false);
    }, 1500 + Math.random() * 1000);
  };

  const totalPnL = state.positions.reduce((sum, p) => sum + p.pnl, 0);

  // Platform Selection Modal
  const PlatformModal = () => (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold">Select Platform</h2>
          <button onClick={() => setShowPlatformModal(false)} className="text-gray-500 hover:text-white">✕</button>
        </div>
        
        <div className="overflow-auto p-4 max-h-[60vh]">
          {Object.entries(PLATFORM_CATEGORIES).map(([category, platformIds]) => (
            <div key={category} className="mb-6">
              <h3 className="text-sm text-gray-500 mb-3">{category}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {platformIds.map(id => {
                  const platform = state.platforms.find(p => p.id === id);
                  if (!platform) return null;
                  
                  return (
                    <button
                      key={id}
                      onClick={() => {
                        if (platform.connected) {
                          setSelectedPlatform(id);
                          setShowPlatformModal(false);
                        } else {
                          setConnectingPlatform(platform);
                          setShowConnectModal(true);
                          setShowPlatformModal(false);
                        }
                      }}
                      className={`p-3 rounded-lg text-left transition-all ${
                        selectedPlatform === id
                          ? 'bg-orange-500/20 border-2 border-orange-500'
                          : platform.connected
                            ? 'bg-gray-800 hover:bg-gray-700 border-2 border-transparent'
                            : 'bg-gray-800/50 hover:bg-gray-800 border-2 border-dashed border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{PLATFORM_ICONS[id] || '📊'}</span>
                        <div>
                          <div className="font-medium text-sm">{platform.name}</div>
                          {platform.connected ? (
                            <div className="text-xs text-green-400">
                              ● {platform.balance?.toLocaleString()} {platform.currency}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500">Click to connect</div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Connect Modal
  const ConnectModal = () => {
    if (!connectingPlatform) return null;
    const fields = getConnectionFields(connectingPlatform.id);
    
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-xl w-full max-w-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{PLATFORM_ICONS[connectingPlatform.id]}</span>
              <h2 className="text-lg font-bold">Connect {connectingPlatform.name}</h2>
            </div>
            <button onClick={() => { setShowConnectModal(false); setConnectionError(''); }} className="text-gray-500">✕</button>
          </div>
          
          <div className="p-4 space-y-4">
            {fields.length === 0 ? (
              <p className="text-gray-400">No credentials needed. Click connect to enable.</p>
            ) : (
              fields.map(field => (
                <div key={field.key}>
                  <label className="text-sm text-gray-400 block mb-1">{field.name}</label>
                  <input
                    type={field.type}
                    placeholder={field.placeholder}
                    value={credentials[field.key] || ''}
                    onChange={e => setCredentials({ ...credentials, [field.key]: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 focus:border-orange-500 focus:outline-none"
                  />
                </div>
              ))
            )}
            
            {connectionError && (
              <div className="text-red-400 text-sm bg-red-900/20 p-3 rounded">{connectionError}</div>
            )}
            
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 rounded font-bold disabled:opacity-50"
            >
              {connecting ? 'Connecting...' : 'Connect'}
            </button>
            
            {connectingPlatform.id === 'mt5' && (
              <p className="text-xs text-gray-500">
                Note: MT5 connection requires the K.I.T. Bridge running locally or on a server.
                <a href="#" className="text-orange-400 ml-1">Learn more →</a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
        {/* Mobile Nav */}
        <nav className="bg-gray-900 border-b border-gray-800 px-2 py-1.5 flex items-center justify-between text-xs">
          <Link to="/" className="flex items-center gap-1 text-cyan-400">
            <span className="w-5 h-5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded flex items-center justify-center text-white text-[10px]">K</span>
          </Link>
          <div className="flex items-center gap-1">
            <Link to="/forum" className="px-2 py-1 rounded hover:bg-gray-800">💬</Link>
            <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400">🖥️</span>
            <Link to="/journal" className="px-2 py-1 rounded hover:bg-gray-800">📓</Link>
            <Link to="/leaderboard" className="px-2 py-1 rounded hover:bg-gray-800">🏆</Link>
          </div>
        </nav>
        {/* Header */}
        <div className="bg-gray-900 border-b border-gray-800 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-orange-500 font-bold">Terminal</span>
              <button onClick={() => setShowSymbolPicker(true)} className="bg-gray-800 px-2 py-1 rounded text-sm">
                {selectedSymbol.symbol} ▼
              </button>
            </div>
            <div className="text-right">
              <div className="text-lg font-mono">${selectedSymbol.price?.toLocaleString()}</div>
              <div className={`text-xs ${(selectedSymbol.change || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(selectedSymbol.change || 0) >= 0 ? '▲' : '▼'} {Math.abs(selectedSymbol.change || 0)}%
              </div>
            </div>
          </div>
          <div className="flex gap-1 mt-2 overflow-x-auto">
            {['1', '5', '15', '60', '240', 'D'].map(tf => (
              <button key={tf} onClick={() => setTimeframe(tf)} className={`px-3 py-1 text-xs rounded ${timeframe === tf ? 'bg-orange-500' : 'bg-gray-800'}`}>
                {tf === '60' ? '1H' : tf === '240' ? '4H' : tf === 'D' ? '1D' : tf + 'm'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          {(['chart', 'order', 'positions', 'pine'] as const).map(tab => (
            <button key={tab} onClick={() => setMobileTab(tab)} className={`flex-1 py-3 text-sm ${mobileTab === tab ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}>
              {tab === 'chart' ? '📈' : tab === 'order' ? '💱' : tab === 'positions' ? '📊' : '🌲'} {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto">
          {mobileTab === 'chart' && <div className="h-[60vh]" ref={chartRef} />}
          
          {mobileTab === 'order' && (
            <div className="p-4 space-y-4">
              <button onClick={() => setShowPlatformModal(true)} className="w-full p-3 bg-gray-800 rounded flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{PLATFORM_ICONS[selectedPlatform]}</span>
                  <span>{currentPlatform?.name}</span>
                </div>
                <span className="text-gray-500">Change ▼</span>
              </button>
              
              {currentPlatform?.connected && (
                <div className="text-sm text-gray-400">Balance: {currentPlatform.balance?.toLocaleString()} {currentPlatform.currency}</div>
              )}
              
              <div className="flex gap-2">
                <button onClick={() => setOrderType('market')} className={`flex-1 py-2 rounded ${orderType === 'market' ? 'bg-gray-700' : 'bg-gray-900'}`}>Market</button>
                <button onClick={() => setOrderType('limit')} className={`flex-1 py-2 rounded ${orderType === 'limit' ? 'bg-gray-700' : 'bg-gray-900'}`}>Limit</button>
              </div>
              
              <div className="flex gap-2">
                <button onClick={() => setOrderSide('buy')} className={`flex-1 py-3 rounded font-bold ${orderSide === 'buy' ? 'bg-green-600' : 'bg-gray-800 text-green-400'}`}>BUY</button>
                <button onClick={() => setOrderSide('sell')} className={`flex-1 py-3 rounded font-bold ${orderSide === 'sell' ? 'bg-red-600' : 'bg-gray-800 text-red-400'}`}>SELL</button>
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Amount</label>
                <input type="text" value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 mt-1" />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Stop Loss</label>
                  <input type="text" value={stopLoss} onChange={e => setStopLoss(e.target.value)} placeholder="Optional" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Take Profit</label>
                  <input type="text" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} placeholder="Optional" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 mt-1" />
                </div>
              </div>
              
              <button onClick={handleOrder} className={`w-full py-4 rounded font-bold text-lg ${orderSide === 'buy' ? 'bg-green-600' : 'bg-red-600'}`}>
                {orderSide.toUpperCase()} {selectedSymbol.symbol}
              </button>
            </div>
          )}
          
          {mobileTab === 'positions' && (
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <span>Total P&L</span>
                <span className={totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}>{totalPnL >= 0 ? '+' : ''}{totalPnL.toFixed(2)}</span>
              </div>
              {state.positions.map(pos => (
                <div key={pos.id} className="bg-gray-900 rounded p-3 mb-2">
                  <div className="flex justify-between">
                    <div>
                      <span className={`text-xs px-1 rounded mr-2 ${pos.side === 'long' ? 'bg-green-600' : 'bg-red-600'}`}>{pos.side.toUpperCase()}</span>
                      {pos.symbol}
                    </div>
                    <span className={pos.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>{pos.pnl >= 0 ? '+' : ''}{pos.pnl.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{pos.size} @ {pos.entryPrice}</span>
                    <button onClick={() => closePosition(pos.id)} className="text-red-400">Close</button>
                  </div>
                </div>
              ))}
              {state.positions.length === 0 && <div className="text-center text-gray-500 py-8">No open positions</div>}
            </div>
          )}
          
          {mobileTab === 'pine' && (
            <div className="p-4 space-y-4">
              <button onClick={() => setShowScriptManager(true)} className="w-full bg-orange-500/20 border border-orange-500/50 text-orange-400 py-3 rounded-lg flex items-center justify-center gap-2">
                📁 My Scripts & Robots ({pineStorage.scripts.length})
              </button>
              <select value={selectedTemplate} onChange={e => { setSelectedTemplate(e.target.value); setPineScript(PINE_TEMPLATES[e.target.value as keyof typeof PINE_TEMPLATES].code); }} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2">
                {Object.entries(PINE_TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
              </select>
              <textarea value={pineScript} onChange={e => setPineScript(e.target.value)} className="w-full h-40 bg-gray-900 border border-gray-700 rounded p-3 font-mono text-xs" placeholder="// Write your Pine Script here..." />
              <button 
                onClick={() => {
                  const name = prompt('Script name:', 'My Strategy');
                  if (name) {
                    const isRobot = confirm('Auto-trading robot? (OK = Yes)');
                    const newScript = { id: Date.now().toString(), name, code: pineScript, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), isRobot };
                    const newStorage = { ...pineStorage, scripts: [...pineStorage.scripts, newScript] };
                    setPineStorage(newStorage);
                    savePineStorage(newStorage);
                    alert('✅ Saved!');
                  }
                }}
                className="w-full bg-orange-600 py-3 rounded font-bold mb-2"
              >
                💾 SAVE SCRIPT
              </button>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={handleParse} className="bg-blue-600 py-2 rounded">Parse</button>
                <button onClick={handleBacktest} className="bg-purple-600 py-2 rounded">Backtest</button>
                <button onClick={() => setPineStatus(pineStatus === 'running' ? 'idle' : 'running')} className={pineStatus === 'running' ? 'bg-red-600 py-2 rounded' : 'bg-green-600 py-2 rounded'}>
                  {pineStatus === 'running' ? 'Stop' : 'Deploy'}
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Modals */}
        {showSymbolPicker && (
          <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
            <div className="bg-gray-900 p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-bold">Select Symbol</h2>
                <button onClick={() => setShowSymbolPicker(false)}>✕</button>
              </div>
              <input type="text" placeholder="Search..." value={symbolSearch} onChange={e => setSymbolSearch(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 mb-3" />
              <div className="flex gap-2 overflow-x-auto pb-2">
                {['all', 'crypto', 'forex', 'stocks', 'commodities', 'indices'].map(cat => (
                  <button key={cat} onClick={() => setCategoryFilter(cat)} className={`px-3 py-1 rounded text-xs whitespace-nowrap ${categoryFilter === cat ? 'bg-orange-500' : 'bg-gray-800'}`}>{cat}</button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {filteredSymbols.map(s => (
                <button key={s.symbol} onClick={() => { setSelectedSymbol(s); setShowSymbolPicker(false); }} className="w-full px-4 py-3 flex justify-between border-b border-gray-800">
                  <div><div className="font-medium">{s.symbol}</div><div className="text-xs text-gray-500">{s.name}</div></div>
                  <div className="text-right"><div className="font-mono">{s.price}</div><div className={`text-xs ${(s.change||0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(s.change||0) >= 0 ? '+' : ''}{s.change}%</div></div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {showPlatformModal && <PlatformModal />}
        {showConnectModal && <ConnectModal />}
        
        {/* Script Manager Modal */}
        {showScriptManager && (
          <PineScriptManager
            storage={pineStorage}
            onStorageChange={(s) => { setPineStorage(s); savePineStorage(s); }}
            onLoadScript={(script) => { setPineScript(script.code); setShowScriptManager(false); }}
            onClose={() => setShowScriptManager(false)}
            currentCode={pineScript}
          />
        )}
        
        {/* Parse Result Modal */}
        {parseResult && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setParseResult(null)}>
            <div className="bg-gray-900 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">{parseResult.success ? '✅ Parse Result' : '❌ Parse Errors'}</h2>
                <button onClick={() => setParseResult(null)} className="text-gray-500">✕</button>
              </div>
              <div className={`p-4 rounded-lg ${parseResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                <pre className="whitespace-pre-wrap text-sm">{parseResult.message}</pre>
                {parseResult.errors && parseResult.errors.map((e, i) => (
                  <div key={i} className="mt-2 text-red-400 text-sm">• {e}</div>
                ))}
              </div>
              <button onClick={() => setParseResult(null)} className="w-full mt-4 bg-gray-800 py-2 rounded">Close</button>
            </div>
          </div>
        )}
        
        {/* Backtest Modal */}
        {showBacktestModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-xl w-full max-w-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">📊 Backtest Results</h2>
                {!isBacktesting && <button onClick={() => setShowBacktestModal(false)} className="text-gray-500">✕</button>}
              </div>
              
              <div className="mb-4 p-3 bg-gray-800 rounded-lg text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Symbol:</span><span className="text-white">{selectedSymbol.symbol}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Timeframe:</span><span className="text-white">{timeframe === '60' ? '1H' : timeframe === '240' ? '4H' : timeframe}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Period:</span><span className="text-white">Last 6 months</span>
                </div>
              </div>
              
              {isBacktesting ? (
                <div className="text-center py-8">
                  <div className="animate-spin text-4xl mb-4">⚙️</div>
                  <div className="text-gray-400">Running backtest...</div>
                  <div className="text-xs text-gray-500 mt-2">Analyzing {Math.floor(Math.random() * 5000 + 2000)} candles</div>
                </div>
              ) : backtestResult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-4 rounded-lg ${backtestResult.profit >= 0 ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                      <div className="text-xs text-gray-400">Net Profit</div>
                      <div className={`text-2xl font-bold ${backtestResult.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {backtestResult.profit >= 0 ? '+' : ''}${backtestResult.profit.toLocaleString()}
                      </div>
                      <div className={`text-sm ${backtestResult.profitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {backtestResult.profitPercent >= 0 ? '+' : ''}{backtestResult.profitPercent}%
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-800">
                      <div className="text-xs text-gray-400">Win Rate</div>
                      <div className="text-2xl font-bold text-white">{backtestResult.winRate}%</div>
                      <div className="text-sm text-gray-400">{backtestResult.trades} trades</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 rounded-lg bg-gray-800 text-center">
                      <div className="text-xs text-gray-400">Sharpe</div>
                      <div className="font-bold">{backtestResult.sharpe}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-800 text-center">
                      <div className="text-xs text-gray-400">Max DD</div>
                      <div className="font-bold text-red-400">-{backtestResult.maxDrawdown}%</div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-800 text-center">
                      <div className="text-xs text-gray-400">Avg Trade</div>
                      <div className="font-bold">${backtestResult.avgTrade}</div>
                    </div>
                  </div>
                  
                  <button onClick={() => setShowBacktestModal(false)} className="w-full mt-4 bg-orange-600 hover:bg-orange-700 py-3 rounded font-bold">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Navigation Bar */}
      <nav className="bg-gray-900/80 border-b border-gray-800 px-4 py-1.5 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-bold">
            <span className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-blue-600 rounded flex items-center justify-center text-white text-xs">K</span>
            K.I.T.
          </Link>
          <div className="flex items-center gap-1 text-gray-400">
            <Link to="/forum" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white">💬 Forum</Link>
            <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">🖥️ Terminal</span>
            <Link to="/journal" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white">📓 Journal</Link>
            <Link to="/leaderboard" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white">🏆 Leaderboard</Link>
            <Link to="/token" className="px-2 py-1 rounded hover:bg-gray-800 hover:text-white">💎 $KIT</Link>
          </div>
        </div>
        <a href="https://kithub.finance" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300">📦 KitHub</a>
      </nav>
      {/* Header */}
      <div className="border-b border-gray-800 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-orange-500">K.I.T. Terminal</h1>
          <button onClick={() => setShowSymbolPicker(!showSymbolPicker)} className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm flex items-center gap-2 hover:border-gray-600">
            <span className="font-medium">{selectedSymbol.symbol}</span>
            <span className="text-gray-500 text-xs">{selectedSymbol.name}</span>
            <span className="text-gray-500">▼</span>
          </button>
          <div className="flex gap-1">
            {[{ v: '1', l: '1m' }, { v: '5', l: '5m' }, { v: '15', l: '15m' }, { v: '60', l: '1H' }, { v: '240', l: '4H' }, { v: 'D', l: '1D' }, { v: 'W', l: '1W' }].map(tf => (
              <button key={tf.v} onClick={() => setTimeframe(tf.v)} className={`px-2 py-1 text-xs rounded ${timeframe === tf.v ? 'bg-orange-500' : 'bg-gray-800 hover:bg-gray-700'}`}>{tf.l}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-2xl font-mono text-green-400">${selectedSymbol.price?.toLocaleString()}</span>
          <span className={`${(selectedSymbol.change || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(selectedSymbol.change || 0) >= 0 ? '▲' : '▼'} {Math.abs(selectedSymbol.change || 0)}%</span>
        </div>
      </div>
      
      <div className="flex h-[calc(100vh-52px)]">
        {/* Chart */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1" ref={chartRef} />
          
          {/* Pine Editor */}
          <div className={`border-t border-gray-800 ${showPineEditor ? 'h-64' : 'h-10'} transition-all`}>
            <div onClick={() => setShowPineEditor(!showPineEditor)} className="flex items-center justify-between px-4 py-2 bg-gray-900/50 cursor-pointer hover:bg-gray-900">
              <div className="flex items-center gap-3">
                <span className="text-green-400">🌲</span>
                <span>Pine Script Editor</span>
                {pineStatus === 'running' && <span className="text-xs bg-green-600 px-2 py-0.5 rounded animate-pulse">● RUNNING</span>}
              </div>
              <span>{showPineEditor ? '▼' : '▲'}</span>
            </div>
            {showPineEditor && (
              <div className="flex h-[calc(100%-40px)]">
                <div className="w-44 bg-gray-900/50 p-2 border-r border-gray-800 overflow-auto">
                  <button onClick={() => setShowScriptManager(true)} className="w-full bg-orange-500/20 border border-orange-500/50 text-orange-400 py-2 rounded text-xs mb-3 hover:bg-orange-500/30">
                    📁 My Scripts ({pineStorage.scripts.length})
                  </button>
                  <div className="text-xs text-gray-500 mb-2">TEMPLATES</div>
                  {Object.entries(PINE_TEMPLATES).map(([k, v]) => (
                    <button key={k} onClick={() => { setSelectedTemplate(k); setPineScript(v.code); }} className={`w-full text-left px-2 py-1 rounded text-xs mb-1 ${selectedTemplate === k ? 'bg-orange-500/20 text-orange-400' : 'hover:bg-gray-800'}`}>{v.name}</button>
                  ))}
                  {pineStorage.activeRobots.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-500 mb-2">🤖 ACTIVE ROBOTS</div>
                      {pineStorage.activeRobots.map(id => {
                        const script = pineStorage.scripts.find(s => s.id === id);
                        return script ? (
                          <div key={id} className="text-xs bg-green-900/30 border border-green-600/30 rounded px-2 py-1 mb-1">
                            <div className="text-green-400 truncate">● {script.name}</div>
                            <div className="text-gray-500 text-[10px]">{script.settings?.symbol}</div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                <textarea value={pineScript} onChange={e => setPineScript(e.target.value)} className="flex-1 bg-[#1a1a2e] p-3 font-mono text-sm resize-none focus:outline-none" />
                <div className="w-28 bg-gray-900/50 p-2 flex flex-col gap-2">
                  <button 
                    onClick={() => {
                      const name = prompt('Script name:', 'My Strategy');
                      if (name) {
                        const isRobot = confirm('Is this an auto-trading robot? (OK = Yes, Cancel = Indicator only)');
                        const newScript = {
                          id: Date.now().toString(),
                          name,
                          code: pineScript,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                          isRobot,
                        };
                        const newStorage = { ...pineStorage, scripts: [...pineStorage.scripts, newScript] };
                        setPineStorage(newStorage);
                        savePineStorage(newStorage);
                        alert('✅ Script saved! Find it in "My Scripts"');
                      }
                    }}
                    className="bg-orange-600 hover:bg-orange-700 px-2 py-2 rounded text-xs font-bold"
                  >
                    💾 Save
                  </button>
                  <button onClick={handleParse} className="bg-blue-600 hover:bg-blue-700 px-2 py-2 rounded text-xs">Parse</button>
                  <button onClick={handleBacktest} className="bg-purple-600 hover:bg-purple-700 px-2 py-2 rounded text-xs">Backtest</button>
                  <button onClick={() => setPineStatus(pineStatus === 'running' ? 'idle' : 'running')} className={`px-2 py-2 rounded text-xs ${pineStatus === 'running' ? 'bg-red-600' : 'bg-green-600'}`}>{pineStatus === 'running' ? 'Stop' : 'Deploy'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="w-80 border-l border-gray-800 flex flex-col">
          {/* Platform */}
          <div className="p-4 border-b border-gray-800">
            <div className="flex justify-between items-center mb-2">
              <div className="text-xs text-gray-500">PLATFORM</div>
              <button onClick={() => setShowPlatformModal(true)} className="text-xs text-orange-400">All Platforms →</button>
            </div>
            <button onClick={() => setShowPlatformModal(true)} className="w-full p-3 bg-gray-800 rounded-lg flex items-center justify-between hover:bg-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-xl">{PLATFORM_ICONS[selectedPlatform]}</span>
                <div className="text-left">
                  <div className="font-medium">{currentPlatform?.name}</div>
                  {currentPlatform?.connected ? (
                    <div className="text-xs text-green-400">● {currentPlatform.balance?.toLocaleString()} {currentPlatform.currency}</div>
                  ) : (
                    <div className="text-xs text-gray-500">Not connected</div>
                  )}
                </div>
              </div>
              <span className="text-gray-500">▼</span>
            </button>
            
            {/* Quick connected platforms */}
            {connectedPlatforms.length > 1 && (
              <div className="flex gap-1 mt-2 overflow-x-auto">
                {connectedPlatforms.slice(0, 5).map(p => (
                  <button key={p.id} onClick={() => setSelectedPlatform(p.id)} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${selectedPlatform === p.id ? 'bg-orange-500/20 border border-orange-500' : 'bg-gray-800'}`}>
                    {PLATFORM_ICONS[p.id]} {p.name.split(' ')[0]}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Order */}
          <div className="p-4 border-b border-gray-800">
            <div className="text-xs text-gray-500 mb-2">ORDER</div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setOrderType('market')} className={`flex-1 py-1.5 text-sm rounded ${orderType === 'market' ? 'bg-gray-700' : 'bg-gray-900'}`}>Market</button>
              <button onClick={() => setOrderType('limit')} className={`flex-1 py-1.5 text-sm rounded ${orderType === 'limit' ? 'bg-gray-700' : 'bg-gray-900'}`}>Limit</button>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setOrderSide('buy')} className={`flex-1 py-2.5 rounded font-bold ${orderSide === 'buy' ? 'bg-green-600' : 'bg-gray-800 text-green-400'}`}>BUY</button>
              <button onClick={() => setOrderSide('sell')} className={`flex-1 py-2.5 rounded font-bold ${orderSide === 'sell' ? 'bg-red-600' : 'bg-gray-800 text-red-400'}`}>SELL</button>
            </div>
            <div className="mb-3">
              <label className="text-xs text-gray-500">Amount</label>
              <input type="text" value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm mt-1" />
              <div className="text-xs text-gray-500 mt-1">≈ ${(parseFloat(amount || '0') * (selectedSymbol.price || 0)).toLocaleString()}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-xs text-gray-500">Stop Loss</label>
                <input type="text" value={stopLoss} onChange={e => setStopLoss(e.target.value)} placeholder="Optional" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm mt-1" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Take Profit</label>
                <input type="text" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} placeholder="Optional" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm mt-1" />
              </div>
            </div>
            <button onClick={handleOrder} className={`w-full py-3 rounded font-bold text-lg ${orderSide === 'buy' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
              {orderSide.toUpperCase()} {selectedSymbol.symbol}
            </button>
          </div>
          
          {/* Positions */}
          <div className="flex-1 overflow-auto p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="text-xs text-gray-500">POSITIONS ({state.positions.length})</div>
              <div className={`font-mono ${totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>{totalPnL >= 0 ? '+' : ''}{totalPnL.toFixed(2)} USD</div>
            </div>
            <div className="space-y-2">
              {state.positions.map(pos => (
                <div key={pos.id} className="bg-gray-900 rounded p-3">
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${pos.side === 'long' ? 'bg-green-600' : 'bg-red-600'}`}>{pos.side.toUpperCase()}</span>
                      <span className="font-medium">{pos.symbol}</span>
                    </div>
                    <span className={`font-mono ${pos.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.pnl >= 0 ? '+' : ''}{pos.pnl.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-gray-500 flex justify-between">
                    <span>{pos.size} @ {pos.entryPrice}</span>
                    <button onClick={() => closePosition(pos.id)} className="text-red-400 hover:text-red-300">✕ Close</button>
                  </div>
                </div>
              ))}
              {state.positions.length === 0 && <div className="text-center text-gray-600 py-8">No open positions</div>}
            </div>
            {state.positions.length > 0 && (
              <button onClick={() => { if (confirm('Close all?')) setState({ ...state, positions: [] }); }} className="w-full mt-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded text-sm">Close All</button>
            )}
          </div>
        </div>
      </div>
      
      {/* Symbol Picker */}
      {showSymbolPicker && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowSymbolPicker(false)} />
          <div className="absolute top-14 left-48 w-96 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
            <div className="p-3 border-b border-gray-800">
              <input type="text" placeholder="Search symbols..." value={symbolSearch} onChange={e => setSymbolSearch(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm" autoFocus />
              <div className="flex gap-1 mt-2 flex-wrap">
                {['all', 'crypto', 'forex', 'stocks', 'commodities', 'indices'].map(cat => (
                  <button key={cat} onClick={() => setCategoryFilter(cat)} className={`px-2 py-1 rounded text-xs ${categoryFilter === cat ? 'bg-orange-500' : 'bg-gray-800'}`}>{cat}</button>
                ))}
              </div>
            </div>
            <div className="max-h-80 overflow-auto">
              {filteredSymbols.map(s => (
                <button key={s.symbol} onClick={() => { setSelectedSymbol(s); setShowSymbolPicker(false); }} className="w-full px-3 py-2 flex justify-between hover:bg-gray-800 text-left">
                  <div><div className="font-medium">{s.symbol}</div><div className="text-xs text-gray-500">{s.name} • {s.exchange}</div></div>
                  <div className="text-right"><div className="font-mono text-sm">{s.price}</div><div className={`text-xs ${(s.change||0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(s.change||0) >= 0 ? '+' : ''}{s.change}%</div></div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      
      {showPlatformModal && <PlatformModal />}
      {showConnectModal && <ConnectModal />}
      {showScriptManager && (
        <PineScriptManager
          storage={pineStorage}
          onStorageChange={(s) => { setPineStorage(s); savePineStorage(s); }}
          onLoadScript={(script) => setPineScript(script.code)}
          onClose={() => setShowScriptManager(false)}
          currentCode={pineScript}
        />
      )}
      
      {/* Parse Result Modal */}
      {parseResult && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setParseResult(null)}>
          <div className="bg-gray-900 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">{parseResult.success ? '✅ Parse Result' : '❌ Parse Errors'}</h2>
              <button onClick={() => setParseResult(null)} className="text-gray-500">✕</button>
            </div>
            <div className={`p-4 rounded-lg ${parseResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
              <pre className="whitespace-pre-wrap text-sm">{parseResult.message}</pre>
              {parseResult.errors && parseResult.errors.map((e, i) => (
                <div key={i} className="mt-2 text-red-400 text-sm">• {e}</div>
              ))}
            </div>
            <button onClick={() => setParseResult(null)} className="w-full mt-4 bg-gray-800 py-2 rounded">Close</button>
          </div>
        </div>
      )}
      
      {/* Backtest Modal */}
      {showBacktestModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">📊 Backtest Results</h2>
              {!isBacktesting && <button onClick={() => setShowBacktestModal(false)} className="text-gray-500">✕</button>}
            </div>
            
            <div className="mb-4 p-3 bg-gray-800 rounded-lg text-sm">
              <div className="flex justify-between text-gray-400">
                <span>Symbol:</span><span className="text-white">{selectedSymbol.symbol}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Timeframe:</span><span className="text-white">{timeframe === '60' ? '1H' : timeframe === '240' ? '4H' : timeframe}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Period:</span><span className="text-white">Last 6 months</span>
              </div>
            </div>
            
            {isBacktesting ? (
              <div className="text-center py-8">
                <div className="animate-spin text-4xl mb-4">⚙️</div>
                <div className="text-gray-400">Running backtest...</div>
                <div className="text-xs text-gray-500 mt-2">Analyzing {Math.floor(Math.random() * 5000 + 2000)} candles</div>
              </div>
            ) : backtestResult && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-4 rounded-lg ${backtestResult.profit >= 0 ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                    <div className="text-xs text-gray-400">Net Profit</div>
                    <div className={`text-2xl font-bold ${backtestResult.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {backtestResult.profit >= 0 ? '+' : ''}${backtestResult.profit.toLocaleString()}
                    </div>
                    <div className={`text-sm ${backtestResult.profitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {backtestResult.profitPercent >= 0 ? '+' : ''}{backtestResult.profitPercent}%
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-gray-800">
                    <div className="text-xs text-gray-400">Win Rate</div>
                    <div className="text-2xl font-bold text-white">{backtestResult.winRate}%</div>
                    <div className="text-sm text-gray-400">{backtestResult.trades} trades</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 rounded-lg bg-gray-800 text-center">
                    <div className="text-xs text-gray-400">Sharpe</div>
                    <div className="font-bold">{backtestResult.sharpe}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-800 text-center">
                    <div className="text-xs text-gray-400">Max DD</div>
                    <div className="font-bold text-red-400">-{backtestResult.maxDrawdown}%</div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-800 text-center">
                    <div className="text-xs text-gray-400">Avg Trade</div>
                    <div className="font-bold">${backtestResult.avgTrade}</div>
                  </div>
                </div>
                
                <button onClick={() => setShowBacktestModal(false)} className="w-full mt-4 bg-orange-600 hover:bg-orange-700 py-3 rounded font-bold">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
